import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';

type NotificationApiResponse = {
  data?: unknown;
};

export interface PortalNotification {
  id: number;
  title: string;
  message: string;
  timeAgo: string;
  isViewed: boolean;
  serviceRequestId?: number;
  serviceRequestTicket?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly getMineUrl = `${environment.apiUrl}/Notifications/GetMine`;
  private readonly markViewedUrl = `${environment.apiUrl}/Notifications/MarkViewed`;

  getMine(): Observable<PortalNotification[]> {
    return this.http.get<NotificationApiResponse>(this.getMineUrl).pipe(
      map((response) => (Array.isArray(response?.data) ? response.data : [])),
      map((items: any[]) => items.map((item) => this.mapNotification(item))),
      catchError(() => of([])),
    );
  }

  markViewed(id: number): Observable<boolean> {
    return this.http.post<{ success?: boolean }>(this.markViewedUrl, { id }).pipe(
      map((response) => !!response?.success),
      catchError(() => of(false)),
    );
  }

  private mapNotification(item: any): PortalNotification {
    const ticket = String(item?.ticketNumber || item?.serviceRequestTicket || item?.referenceCode || '').trim();
    const title = String(item?.title || item?.notificationTitle || '').trim();
    const viewedOn = String(item?.viewedOn || '').trim();
    const isViewed = Boolean(viewedOn);
    const content = String(item?.message || item?.notificationText || item?.notificationContent || item?.description || '').trim();

    return {
      id: Number(item?.id) || 0,
      title: title || (ticket ? `Service Request ${ticket}` : 'Notification'),
      message: content,
      timeAgo: this.toTimeAgo(String(item?.createdDate || item?.createdOn || '')),
      isViewed,
      serviceRequestId: Number(item?.serviceRequestId || item?.referenceId || item?.entityId) || undefined,
      serviceRequestTicket: ticket || undefined,
    };
  }

  private toTimeAgo(value: string): string {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    const diffInMs = Date.now() - date.getTime();
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (diffInMs < hour) {
      const minutes = Math.max(1, Math.floor(diffInMs / minute));
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }

    if (diffInMs < day) {
      const hours = Math.max(1, Math.floor(diffInMs / hour));
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }

    const days = Math.max(1, Math.floor(diffInMs / day));
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
}
